import React from 'react'
import { SubmitHandler } from 'react-hook-form'
import ControlledAutocompleteShipper from 'transfix-form/components/ControlledAutocompleteShipper'
import { Button } from 'transfix-ui/components/Button'
import { Box, Grid, makeStyles } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { IShippersQueryVariables } from '../../../ShippersPage/graphql/Shippers'
import { ControlledStatusSelect } from './components'
import { useShipperFilterForm } from './hooks'
import { TShipperFilterSchema } from './schemas'

const useShipperFilterFormStyles = makeStyles(theme => ({
  root: {},
  container: {
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
}))

const getDefaultValues = (defaultValues?: IShippersQueryVariables): TShipperFilterSchema => {
  const { searchTerm: searchTermText, state } = defaultValues || {}
  const searchTerm = searchTermText
    ? {
        id: '-1',
        text: searchTermText || '',
      }
    : null

  return {
    searchTerm,
    state: state || '',
  }
}

export interface IShipperFilterFormProps {
  /**
   * Handles filter query params change
   */
  onSubmitSuccess: SubmitHandler<TShipperFilterSchema>
  /**
   * Resets filters and pagination
   */
  onReset: () => void
  defaultValues?: IShippersQueryVariables
}

const ShipperFilterForm = ({
  defaultValues: defaultValuesProp,
  onReset,
  onSubmitSuccess,
}: IShipperFilterFormProps) => {
  const defaultValues = getDefaultValues(defaultValuesProp)
  const { isMobile } = useBreakpointContext()
  const classes = useShipperFilterFormStyles()
  const { control, handleSubmit, reset } = useShipperFilterForm({ defaultValues })

  const clearHandler = () => {
    reset({ searchTerm: null, state: '' })
    onReset()
  }

  const submitHandler = handleSubmit(onSubmitSuccess)
  const changeHandler = () => submitHandler()

  return (
    <form onSubmit={submitHandler} data-test='ShipperFilterForm' className={classes.root}>
      <Grid container spacing={isMobile ? 1 : 2} alignItems='flex-end'>
        <Grid item xs={12} md='auto'>
          <Box minWidth='150px'>
            <ControlledStatusSelect control={control} onChange={changeHandler} />
          </Box>
        </Grid>
        <Grid item xs={12} md='auto'>
          <Box minWidth='250px'>
            <ControlledAutocompleteShipper
              control={control}
              name='searchTerm'
              AutocompleteShipperProps={{
                placeholder: 'Search',
                dataTest: 'search-input',
                label: 'Shipper name',
                size: 'small',
                onChange: changeHandler,
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md='auto'>
          <Grid container spacing={isMobile ? 1 : 2} alignItems='flex-end'>
            <Grid item xs={12} md='auto'>
              <Button color='tertiary' onClick={clearHandler} fullWidth={isMobile}>
                Clear search
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}

export default ShipperFilterForm
